/**
 * Ca'Lefort 通用轮播组件
 * 支持逐卡滚动、可配置停靠点数量，以及首尾对称留白。
 */
(function (window) {
  'use strict';

  var defaults = {
    breakpoint: '(min-width: 768px)',
    slideSelector: '.x-carousel__slide',
    dotClass: 'x-carousel__dot',
    arrowPrev: '[data-prev]',
    arrowNext: '[data-next]',
    track: '[data-track]',
    viewport: '[data-viewport]',
    dots: '[data-dots]',
    ariaLabel: 'Go to page',
    stepMode: 'item',
    desktopDotCount: null,
    onInit: null
  };

  function mergeOptions(options) {
    var merged = {};
    for (var key in defaults) {
      if (defaults.hasOwnProperty(key)) {
        merged[key] = options.hasOwnProperty(key) ? options[key] : defaults[key];
      }
    }
    return merged;
  }

  function init(section, options) {
    if (!section) return;

    var config = mergeOptions(options || {});

    var track = section.querySelector(config.track);
    var viewport = section.querySelector(config.viewport);
    var prev = section.querySelector(config.arrowPrev);
    var next = section.querySelector(config.arrowNext);
    var dotsContainer = section.querySelector(config.dots);

    if (!track) return;

    // transform 位移值（仅 transform 模式使用）
    var offset = 0;

    var slides = function () {
      return Array.from(track.querySelectorAll(config.slideSelector));
    };

    var slideGap = function () {
      return parseFloat(getComputedStyle(track).gap) || 0;
    };

    var slideWidth = function () {
      var slide = slides()[0];
      if (!slide) return 0;
      return slide.offsetWidth + slideGap();
    };

    var visibleCount = function () {
      var slide = slides()[0];
      if (!slide) return 1;
      var gap = slideGap();
      var width = slide.offsetWidth + gap;
      if (!width) return 1;
      var rawCount = (viewWidth() + gap) / width;
      var roundedCount = Math.round(rawCount);

      // 百分比卡片宽度可能在 offsetWidth 中产生约 1px 的取整误差。
      // 当结果非常接近整数时按整数处理，避免桌面端 2 列被误判为 1 列。
      if (Math.abs(rawCount - roundedCount) < 0.02) return roundedCount;
      return Math.floor(rawCount);
    };

    var maxScroll = function () {
      return track.scrollWidth - track.clientWidth;
    };

    var contentEnd = function () {
      var count = slides().length;
      if (count === 0) return 0;
      var slide = slides()[0];
      var gap = slideGap();
      return (count - 1) * (slide.offsetWidth + gap) + slide.offsetWidth;
    };

    var maxScrollLeft = function () {
      if (isTransformMode()) {
        return Math.max(0, trackPaddingLeft() + contentEnd() + trackPaddingRight() - viewport.clientWidth);
      }
      return Math.max(0, contentEnd() - viewWidth());
    };

    var setOffset = function (value, smooth) {
      offset = Math.min(Math.max(value, 0), maxScrollLeft());
      if (smooth) {
        track.style.transform = 'translate3d(' + -offset + 'px, 0, 0)';
      } else {
        track.style.transition = 'none';
        track.style.transform = 'translate3d(' + -offset + 'px, 0, 0)';
        // 强制 reflow，确保无过渡立即生效
        void track.offsetWidth;
        track.style.transition = '';
      }
    };

    var isPageMode = function () {
      return window.matchMedia(config.breakpoint).matches;
    };

    // 存在 viewport 容器且处于 PC 分页断点时，使用 translate3d 位移替代原生滚动，
    // 配合 viewport 的 overflow: hidden 与 track 的左 padding，实现卡片左侧 peek 不被容器裁剪
    var isTransformMode = function () {
      return !!viewport && isPageMode();
    };

    var trackPaddingLeft = function () {
      if (!isTransformMode()) return 0;
      return parseFloat(getComputedStyle(track).paddingLeft) || 0;
    };

    // 卡片可见宽度不包含左右安全留白。
    var viewWidth = function () {
      if (isTransformMode()) {
        return viewport.clientWidth - trackPaddingLeft() - trackPaddingRight();
      }
      return track.clientWidth;
    };

    var trackPaddingRight = function () {
      if (!isTransformMode()) return 0;
      return parseFloat(getComputedStyle(track).paddingRight) || 0;
    };

    var currentScroll = function () {
      return isTransformMode() ? offset : track.scrollLeft;
    };

    var pageCount = function () {
      var count = slides().length;
      if (!isPageMode() || count <= 1) return count;
      var visible = visibleCount();
      if (visible <= 0) return 1;
      if (config.stepMode === 'page') {
        return Math.max(1, Math.ceil(count / visible));
      }
      var naturalCount = Math.max(1, count - visible + 1);
      if (config.desktopDotCount !== null) {
        return Math.max(1, Math.min(count, parseInt(config.desktopDotCount, 10) || naturalCount));
      }
      return naturalCount;
    };

    var targetScrollForPage = function (pageIndex) {
      // 移动端使用原生滚动时，按卡片的真实位置对齐，避免等分最大滚动距离
      // 导致卡片左边缘落入滚动容器的裁剪区域。PC transform 分页逻辑保持不变。
      if (!isPageMode()) {
        var targetSlide = slides()[pageIndex];
        if (!targetSlide) return 0;
        var paddingLeft = parseFloat(getComputedStyle(track).paddingLeft) || 0;
        var slideTarget = targetSlide.getBoundingClientRect().left
          - track.getBoundingClientRect().left
          + track.scrollLeft
          - paddingLeft;
        return Math.min(Math.max(slideTarget, 0), maxScroll());
      }

      var max = maxScrollLeft();
      var count = pageCount();
      if (count <= 1) return 0;
      if (config.stepMode === 'page') {
        return Math.min(pageIndex * visibleCount() * slideWidth(), max);
      }
      return max * (pageIndex / (count - 1));
    };

    var currentIndex = function () {
      if (!isPageMode()) {
        var width = slideWidth();
        if (!width) return 0;
        var maxIndex = Math.max(0, slides().length - 1);
        var index = Math.round(track.scrollLeft / width);
        return Math.min(Math.max(index, 0), maxIndex);
      }

      var pages = pageCount();
      if (pages <= 1) return 0;
      var scrollLeft = currentScroll();
      var closest = 0;
      var minDiff = Infinity;
      for (var i = 0; i < pages; i += 1) {
        var target = targetScrollForPage(i);
        var diff = Math.abs(scrollLeft - target);
        if (diff < minDiff) {
          minDiff = diff;
          closest = i;
        }
      }
      return closest;
    };

    var renderDots = function () {
      if (!dotsContainer) return;
      var count = pageCount();
      var active = Math.min(currentIndex(), count - 1);

      dotsContainer.innerHTML = '';
      for (var i = 0; i < count; i += 1) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = config.dotClass + (i === active ? ' is-active' : '');
        dot.setAttribute('aria-label', config.ariaLabel + ' ' + (i + 1));
        dot.setAttribute('data-page', i);
        dot.addEventListener('click', function (index) {
          return function () {
            goToSlide(index);
          };
        }(i));
        dotsContainer.appendChild(dot);
      }
    };

    var updateDots = function () {
      if (!dotsContainer) return;
      var index = currentIndex();
      var dots = dotsContainer.querySelectorAll('[data-page]');
      dots.forEach(function (dot) {
        var page = parseInt(dot.getAttribute('data-page'), 10);
        dot.classList.toggle('is-active', page === index);
      });
    };

    var goToSlide = function (index) {
      var target = targetScrollForPage(index);
      if (isTransformMode()) {
        setOffset(target, true);
        updateControls();
      } else {
        track.scrollTo({ left: target, behavior: 'smooth' });
        updateDots();
      }
    };

    var updateControls = function () {
      var max = isPageMode() ? maxScrollLeft() : maxScroll();
      var current = currentScroll();
      var atStart = current <= 1;
      var atEnd = current >= max - 1;

      if (prev) prev.disabled = atStart;
      if (next) next.disabled = atEnd;

      updateDots();
    };

    var scrollBy = function (direction) {
      var width = slideWidth();
      if (config.stepMode !== 'item' && isPageMode()) {
        width = visibleCount() * slideWidth();
      }
      var target = currentScroll() + direction * width;
      var max = isPageMode() ? maxScrollLeft() : maxScroll();
      var clamped = Math.min(Math.max(target, 0), max);
      if (isTransformMode()) {
        setOffset(clamped, true);
        updateControls();
      } else {
        track.scrollTo({ left: clamped, behavior: 'smooth' });
      }
    };

    if (prev) {
      prev.addEventListener('click', function () {
        if (!prev.disabled) scrollBy(-1);
      });
    }

    if (next) {
      next.addEventListener('click', function () {
        if (!next.disabled) scrollBy(1);
      });
    }

    track.addEventListener('scroll', updateControls, { passive: true });
    window.addEventListener('resize', function () {
      if (viewport) {
        if (isTransformMode()) {
          // 从移动端切换来时清除原生滚动位置，并按新尺寸收敛位移
          if (track.scrollLeft !== 0) track.scrollLeft = 0;
          setOffset(offset, false);
        } else {
          offset = 0;
          track.style.transform = '';
        }
      }
      renderDots();
      updateControls();
    });

    renderDots();
    updateControls();

    if (typeof config.onInit === 'function') {
      config.onInit(section, {
        track: track,
        slides: slides,
        updateControls: updateControls
      });
    }
  }

  window.ClfCarousel = {
    init: init
  };
})(window);
